import type { EarthingMeasurement } from '../services/controlTaskService';
import type { RcdDecimal, RcdMeasurement, RcdMeasurementsByNode } from '../types/rcdMeasurement';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);
const textValue = (value: unknown): string | null => typeof value === 'string' ? value : null;
const decimalValue = (value: unknown): RcdDecimal =>
  typeof value === 'string' || (typeof value === 'number' && Number.isFinite(value)) ? value : null;

/** Deterministic row rule; server preview and save still validate the complete input. */
export const zeroingCompliance = {
  generatedComplianceResult: 'NOT 10',
  complianceResult: 'NOT 10',
  complianceResultOverridden: false,
  complianceCalculationStatus: 'CALCULATED',
  complianceCalculationMessage: 'Sıfırlama tespit edildiği için NOT 10 öncelikli uygulandı.',
} as const;

export const normalizeRcdMeasurementsByNode = (value: unknown): RcdMeasurementsByNode => {
  if (!isRecord(value)) return {};
  const result: RcdMeasurementsByNode = {};
  Object.entries(value).forEach(([nodeId, rows]) => {
    if (!/^[1-9]\d*$/.test(nodeId) || !Array.isArray(rows)) return;
    result[nodeId] = rows.filter(isRecord).map((row): RcdMeasurement => ({
      zeroingDetected: row.zeroingDetected === true,
      ...(Object.prototype.hasOwnProperty.call(row, 'sourceEarthingMeasurementId') ? {
        sourceEarthingMeasurementId: typeof row.sourceEarthingMeasurementId === 'number' ? row.sourceEarthingMeasurementId : null,
      } : {}),
      ...(Object.prototype.hasOwnProperty.call(row, 'sourceEarthingClientKey') ? {
        sourceEarthingClientKey: textValue(row.sourceEarthingClientKey),
      } : {}),
      ...(Object.prototype.hasOwnProperty.call(row, 'generatedComplianceResult') ? {
        generatedComplianceResult: textValue(row.generatedComplianceResult),
      } : {}),
      ...(typeof row.complianceResultOverridden === 'boolean' ? { complianceResultOverridden: row.complianceResultOverridden } : {}),
      ...(typeof row.complianceCalculationStatus === 'string' ? { complianceCalculationStatus: row.complianceCalculationStatus } : {}),
      id: typeof row.id === 'number' ? row.id : undefined,
      reportNodeId: typeof row.reportNodeId === 'number' ? row.reportNodeId : undefined,
      rowOrder: typeof row.rowOrder === 'number' ? row.rowOrder : undefined,
      upstreamPanelName: textValue(row.upstreamPanelName),
      finalCircuitPanelName: textValue(row.finalCircuitPanelName),
      ...(Object.prototype.hasOwnProperty.call(row, 'complianceResult') ? { complianceResult: textValue(row.complianceResult) } : {}),
      ...(typeof row.complianceCalculationMessage === 'string' ? { complianceCalculationMessage: row.complianceCalculationMessage } : {}),
      circuitBreakerType: textValue(row.circuitBreakerType),
      phaseEarthLoopImpedanceOhm: decimalValue(row.phaseEarthLoopImpedanceOhm),
      phaseNeutralLoopImpedanceOhm: decimalValue(row.phaseNeutralLoopImpedanceOhm),
      calculatedThreePhaseShortCircuitCurrentKa: decimalValue(row.calculatedThreePhaseShortCircuitCurrentKa),
      circuitBreakerBreakingCapacityKa: decimalValue(row.circuitBreakerBreakingCapacityKa),
      phaseToPhaseVoltageV: decimalValue(row.phaseToPhaseVoltageV),
      phaseToNeutralVoltageV: decimalValue(row.phaseToNeutralVoltageV),
      neutralToEarthVoltageV: decimalValue(row.neutralToEarthVoltageV),
      rcdLabelInformation: isRecord(row.rcdLabelInformation) ? {
        rcdType: textValue(row.rcdLabelInformation.rcdType),
        ratedCurrentA: decimalValue(row.rcdLabelInformation.ratedCurrentA),
        ratedResidualCurrentMa: decimalValue(row.rcdLabelInformation.ratedResidualCurrentMa),
        tripTimeDelayMs: decimalValue(row.rcdLabelInformation.tripTimeDelayMs),
      } : null,
      usedRcd: isRecord(row.usedRcd) ? {
        rcdType: textValue(row.usedRcd.rcdType),
        ratedResidualCurrentMa: decimalValue(row.usedRcd.ratedResidualCurrentMa),
        testTripTimeMs: decimalValue(row.usedRcd.testTripTimeMs),
        ...(typeof row.usedRcd.testTripTimeDidNotTrip === 'boolean'
          ? { testTripTimeDidNotTrip: row.usedRcd.testTripTimeDidNotTrip } : {}),
      } : null,
      ...(row.zeroingDetected === true ? zeroingCompliance : {}),
    }));
  });
  return result;
};

/** Send complete row values, excluding response-only metadata; retain explicit empty lists. */
export const prepareRcdMeasurementsForSubmit = (value: RcdMeasurementsByNode): RcdMeasurementsByNode => {
  const normalized = normalizeRcdMeasurementsByNode(value);
  return Object.fromEntries(Object.entries(normalized).map(([nodeId, rows]) => [
    nodeId,
    rows.map(({ reportNodeId, rowOrder, generatedComplianceResult, complianceCalculationStatus, complianceCalculationMessage,
      phaseEarthLoopImpedanceOhm, calculatedThreePhaseShortCircuitCurrentKa, ...row }) => row),
  ]));
};

export const validateRcdDecimal = (value: unknown): string | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const text = String(value).trim();
  if (!/^\+?\d+(?:\.\d*)?$/.test(text)) return 'Sıfır veya pozitif bir sayı giriniz.';
  const [integer, fraction = ''] = text.replace(/^\+/, '').split('.');
  if (integer.replace(/^0+/, '').length > 13 || fraction.replace(/0+$/, '').length > 6) {
    return 'En fazla 13 tam ve 6 ondalık basamak girilebilir.';
  }
  return undefined;
};

/** Append missing panel links without replacing user-entered or legacy rows. */
export const completeRcdMeasurements = (
  rows: RcdMeasurement[], sources: EarthingMeasurement[],
  sourceIdsByClientKey: Record<string, number> = {},
): RcdMeasurement[] => {
  const resolvedIds = { ...sourceIdsByClientKey };
  sources.forEach(source => {
    if (source.id != null && source.clientKey) resolvedIds[source.clientKey] = source.id;
  });
  let changed = false;
  const result = rows.map(row => {
    const id = row.sourceEarthingClientKey ? resolvedIds[row.sourceEarthingClientKey] : undefined;
    if (row.sourceEarthingMeasurementId == null && id != null) {
      changed = true;
      return { ...row, sourceEarthingMeasurementId: id, sourceEarthingClientKey: null };
    }
    return row;
  });
  const linkedIds = new Set(result.map(row => row.sourceEarthingMeasurementId).filter(id => id != null));
  const linkedKeys = new Set(result.map(row => row.sourceEarthingClientKey).filter(Boolean));
  sources.forEach(source => {
    if (!['MAIN_PANEL', 'SUB_PANEL', 'FIRE_PANEL'].includes(source.measurementPointGroup ?? '')) return;
    if (source.id != null) {
      if (linkedIds.has(source.id)) return;
      linkedIds.add(source.id);
      result.push({ sourceEarthingMeasurementId: source.id, zeroingDetected: false });
    } else if (source.clientKey) {
      if (linkedKeys.has(source.clientKey)) return;
      linkedKeys.add(source.clientKey);
      result.push({ sourceEarthingClientKey: source.clientKey, zeroingDetected: false });
    } else return;
    changed = true;
  });
  return changed ? result : rows;
};
